import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import type { NotificationReceivedEvent, NotificationActionPerformedEvent } from '@capacitor-firebase/messaging';

/**
 * Initialize Capacitor push notifications for native platforms.
 * Returns the FCM token string, or null if unavailable.
 *
 * Uses @capacitor-firebase/messaging (dynamically imported) which returns a
 * real FCM registration token on both iOS and Android. The prior
 * @capacitor/push-notifications plugin returned an APNs device token on iOS,
 * which FCM cannot accept — so all iOS sends silently failed.
 *
 * The dynamic import keeps the plugin out of the web bundle's evaluation
 * graph, which avoids Vite's optional-peer-dep shim choking on the
 * accompanying @capacitor-firebase plugins.
 */
export async function initCapacitorPush(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) return null;

  const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');

  const permResult = await FirebaseMessaging.requestPermissions();
  if (permResult.receive !== 'granted') {
    console.log('Push notification permission denied');
    return null;
  }

  try {
    const { token } = await FirebaseMessaging.getToken();
    return token || null;
  } catch (error) {
    console.error('FirebaseMessaging.getToken failed:', error);
    return null;
  }
}

export interface NativePushNotification {
  title: string;
  body: string;
  clickAction: string;
}

/**
 * Add listeners for foreground notifications and notification taps.
 * onNavigate: called when user taps a notification (background/killed state).
 * onForeground: called when a notification arrives while app is in foreground.
 * Returns a cleanup function.
 */
export function addPushListeners(
  onNavigate: (path: string) => void,
  onForeground?: (notification: NativePushNotification) => void,
): () => void {
  if (!Capacitor.isNativePlatform()) return () => {};

  let cleanup: (() => void) | null = null;
  let cancelled = false;

  (async () => {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
    if (cancelled) return;

    const foregroundListener = FirebaseMessaging.addListener(
      'notificationReceived',
      (event: NotificationReceivedEvent) => {
        if (onForeground) {
          const n = event.notification;
          const data = (n.data as Record<string, string> | undefined) || {};
          onForeground({
            title: n.title || 'Romy',
            body: n.body || '',
            clickAction: data.click_action || '/',
          });
        }
      },
    );

    const actionListener = FirebaseMessaging.addListener(
      'notificationActionPerformed',
      (event: NotificationActionPerformedEvent) => {
        const data = (event.notification.data as Record<string, string> | undefined) || {};
        const clickAction = data.click_action;
        if (clickAction) {
          onNavigate(clickAction);
        }
      },
    );

    cleanup = () => {
      foregroundListener.then((l: PluginListenerHandle) => l.remove());
      actionListener.then((l: PluginListenerHandle) => l.remove());
    };
  })();

  return () => {
    cancelled = true;
    cleanup?.();
  };
}
